// src/components/Calendar/Calendar.tsx

import type React from "react";
import { useState } from "react";
import { MonthPicker } from "./MonthPicker";
import { YearPicker } from "./YearPicker";
import {
  buildMonthCells,
  DOW_SHORT,
  MONTH_NAMES_SHORT,
  parseIsoToYMD,
  todayIso,
} from "./utils";

interface CalendarProps {
  valueIso?: string;
  onSelect: (iso: string) => void;
  onClear?: () => void;
}

type PickerMode = "day" | "month" | "year";

export function Calendar({ valueIso, onSelect, onClear }: CalendarProps) {
  const today = todayIso();
  const initial = parseIsoToYMD(valueIso);
  const [cursor, setCursor] = useState(initial);
  const [mode, setMode] = useState<PickerMode>("day");

  const cells = buildMonthCells(cursor.year, cursor.month);

  const goPrev = () =>
    setCursor((c) => (c.month === 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: c.month - 1 }));
  const goNext = () =>
    setCursor((c) => (c.month === 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: c.month + 1 }));
  const goToday = () => {
    const t = new Date();
    setCursor({ year: t.getFullYear(), month: t.getMonth() });
    onSelect(today);
  };

  return (
    <div style={popStyle}>
      <div style={headStyle}>
        <button type="button" onClick={goPrev} style={navStyle} aria-label="Previous month">&lsaquo;</button>
        <div style={chipGroupStyle}>
          <button
            type="button"
            onClick={() => setMode((m) => (m === "month" ? "day" : "month"))}
            style={{ ...chipStyle, ...(mode === "month" ? chipActiveStyle : {}) }}
          >
            {MONTH_NAMES_SHORT[cursor.month]}
            <span style={caretStyle}>&#9662;</span>
          </button>
          <button
            type="button"
            onClick={() => setMode((m) => (m === "year" ? "day" : "year"))}
            style={{ ...chipStyle, ...(mode === "year" ? chipActiveStyle : {}) }}
          >
            {cursor.year}
            <span style={caretStyle}>&#9662;</span>
          </button>
        </div>
        <button type="button" onClick={goNext} style={navStyle} aria-label="Next month">&rsaquo;</button>
      </div>

      {mode === "day" && (
        <>
          <div style={gridStyle}>
            {DOW_SHORT.map((d) => <div key={d} style={dowStyle}>{d}</div>)}
            {cells.map((c, i) => {
              const isSelected = valueIso === c.iso;
              const isToday = today === c.iso;
              const bg = isSelected ? "#263238" : (isToday && !c.muted ? "#E7EAF0" : "transparent");
              const color = isSelected ? "#ffffff" : (c.muted ? "#6B7684" : "#0E1A1A");
              return (
                <button
                  type="button"
                  key={i}
                  style={{ ...dayStyle, background: bg, color, fontWeight: isSelected || isToday ? 700 : 500 }}
                  onClick={() => onSelect(c.iso)}
                  onMouseEnter={(e) => { if (!isSelected && !isToday) (e.currentTarget as HTMLElement).style.background = "#F3F5F5"; }}
                  onMouseLeave={(e) => { if (!isSelected && !isToday) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  {c.day}
                </button>
              );
            })}
          </div>
          <div style={footStyle}>
            <button type="button" onClick={goToday} style={{ ...footBtnStyle, color: "#0E1A1A", fontWeight: 600 }}>Today</button>
            {onClear && <button type="button" onClick={onClear} style={footBtnStyle}>Clear</button>}
          </div>
        </>
      )}

      {mode === "month" && (
        <MonthPicker
          currentMonth={cursor.month}
          onSelect={(m) => { setCursor({ ...cursor, month: m }); setMode("day"); }}
        />
      )}

      {mode === "year" && (
        <YearPicker
          currentYear={cursor.year}
          onSelect={(y) => { setCursor({ ...cursor, year: y }); setMode("day"); }}
        />
      )}
    </div>
  );
}

const popStyle: React.CSSProperties = { position: "absolute", top: "calc(100% + 6px)", left: 0, background: "#fff", border: "1px solid #D8DEE6", borderRadius: 12, boxShadow: "0 10px 32px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.05)", padding: 14, zIndex: 20, width: 300 };
const headStyle: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 4 };
const navStyle: React.CSSProperties = { background: "transparent", border: 0, padding: "4px 7px", cursor: "pointer", color: "#263238", fontSize: 15, lineHeight: 1, borderRadius: 5, fontFamily: "inherit" };
const chipGroupStyle: React.CSSProperties = { flex: 1, display: "flex", justifyContent: "center", gap: 6 };
const chipStyle: React.CSSProperties = { background: "#F3F5F5", border: "1px solid #D8DEE6", color: "#0E1A1A", fontWeight: 700, fontSize: 13, padding: "5px 10px", borderRadius: 7, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "inherit" };
const chipActiveStyle: React.CSSProperties = { background: "#263238", color: "#ffffff", borderColor: "#263238" };
const caretStyle: React.CSSProperties = { color: "inherit", fontSize: 10 };
const gridStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 };
const dowStyle: React.CSSProperties = { fontSize: 10.5, color: "#6B7684", fontWeight: 600, textAlign: "center", padding: "4px 0", letterSpacing: "0.03em", textTransform: "uppercase" };
const dayStyle: React.CSSProperties = { padding: "7px 0", textAlign: "center", fontSize: 13, cursor: "pointer", borderRadius: 6, border: 0, fontFamily: "inherit" };
const footStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, paddingTop: 10, borderTop: "1px solid #E7EAF0" };
const footBtnStyle: React.CSSProperties = { background: "transparent", border: 0, color: "#263238", fontWeight: 500, fontSize: 12.5, cursor: "pointer", padding: "4px 0", fontFamily: "inherit" };